<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@/app/i18n'
import { confirm } from '@/shared/composables/useConfirm'

import ModalDocked from './modal/ModalDocked.vue'
import { useOverlayStack } from './overlay-stack'

const props = withDefaults(
  defineProps<{
    title?: string
    size?: 'sm' | 'md' | 'lg' | 'xl'
    /** Legacy modals used `no-close-on-backdrop`; the default keeps that. */
    closeOnBackdrop?: boolean
    /** Close icon in the header (and Escape, unless `closeOnEscape` is false). */
    closable?: boolean
    /** Escape closes the modal — only the top one when modals are nested (interview over status change, R4). */
    closeOnEscape?: boolean
    hideFooter?: boolean
    /** One button (legacy `ok-only`). */
    okOnly?: boolean
    okTitle?: string
    cancelTitle?: string
    okDisabled?: boolean
    okVariant?: 'primary' | 'danger'
    /**
     * Runs on OK. Resolve `false` (or throw) to keep the modal open — legacy `@ok` handlers called
     * `event.preventDefault()` and showed the error; the modal closes only on success.
     */
    okAction?: () => unknown
    /**
     * Docked, non-blocking window bottom-right (07 inbox compose; legacy `modal-sticky hide-backdrop`): the page stays
     * usable, "–" minimises / restores, "×" discards (asking first while `dirty`), `visible = false` hides it and keeps
     * the draft. README "AppModal — docked".
     */
    docked?: boolean
    /** Docked: the window holds unsaved input, so discarding asks first. */
    dirty?: boolean
  }>(),
  {
    title: '',
    size: 'md',
    closeOnBackdrop: false,
    closable: true,
    closeOnEscape: true,
    hideFooter: false,
    okOnly: false,
    okTitle: '',
    cancelTitle: '',
    okDisabled: false,
    okVariant: 'primary',
    okAction: undefined,
    docked: false,
    dirty: true,
  },
)

const visible = defineModel<boolean>('visible', { default: false })
/** Docked: collapsed to its title bar (the content is kept). */
const minimized = defineModel<boolean>('minimized', { default: false })
const emit = defineEmits<{ ok: []; cancel: []; hidden: []; discard: [] }>()
const { t } = useI18n()
const busy = ref(false)
/** Docked: bumped by a discard or a successful OK, which re-creates the content (a fresh draft next time). */
const session = ref(0)
// Escape is handled here, not by the Dialog: with nested modals only the top one closes (PrimeVue closes them all).
// The docked window is not an overlay (non-blocking): it handles Escape itself, inside it only.
const overlayOpen = computed(() => visible.value && !props.docked)
const escape = () => {
  if (props.closable && props.closeOnEscape && !busy.value) cancel()
}
const { isTop } = useOverlayStack(overlayOpen, { onEscape: escape })

/**
 * Escape on a control inside whose own list is closed (a picker after its list was closed with Escape): PrimeVue's
 * select and multi-select mark that key handled anyway, so the stack never saw it and the modal stayed. Bound in the
 * capture phase, as AppPopover's; an open list (`aria-expanded="true"`) still takes the key first.
 */
function onKeydownCapture(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || !isTop.value) return
  const target = event.target as HTMLElement | null
  if (!target?.closest('[aria-expanded="false"]') || target.closest('[aria-expanded="true"]')) return
  event.stopPropagation()
  event.preventDefault()
  escape()
}

const WIDTHS = { sm: '300px', md: '500px', lg: '800px', xl: '1140px' } as const
const style = computed(() => ({ width: WIDTHS[props.size], maxWidth: 'calc(100vw - 2rem)' }))

async function ok(): Promise<void> {
  if (busy.value) return
  emit('ok')
  if (!props.okAction) {
    close()
    return
  }
  busy.value = true
  try {
    const result = await props.okAction()
    if (result !== false) close()
  } catch {
    // The handler reported the error (toast); the modal stays open.
  } finally {
    busy.value = false
  }
}

function close(): void {
  visible.value = false
  if (!props.docked) return
  // Sent: the next opening starts from an empty form.
  minimized.value = false
  session.value++
}

function cancel(): void {
  if (props.docked) {
    void discard()
    return
  }
  emit('cancel')
  visible.value = false
}

/** Docked "×" (and Cancel): asks while `dirty`, then empties and closes the window. */
async function discard(): Promise<void> {
  if (busy.value) return
  if (
    props.dirty &&
    !(await confirm({
      title: t('common.modal.discardTitle'),
      text: t('common.modal.discardText'),
      okText: t('common.modal.discard'),
    }))
  ) {
    return
  }
  emit('discard')
  emit('cancel')
  minimized.value = false
  visible.value = false
  session.value++
}

// The docked window has no Dialog to announce it: `hidden` whenever it goes away (OK, discard, or hidden by the host).
watch(visible, (open, was) => {
  if (props.docked && was && !open) emit('hidden')
})
</script>

<template>
  <ModalDocked
    v-if="docked"
    v-model:visible="visible"
    v-model:minimized="minimized"
    :title="title"
    :width="WIDTHS[size]"
    :session="session"
    :closable="closable"
    :busy="busy"
    :hide-footer="hideFooter"
    @discard="discard"
  >
    <slot />
    <template #footer>
      <slot name="footer" :ok="ok" :cancel="cancel" :busy="busy">
        <Button
          :label="okTitle || t('common.action.confirm')"
          :severity="okVariant === 'danger' ? 'danger' : undefined"
          :disabled="okDisabled"
          :loading="busy"
          data-testid="modal-ok"
          @click="ok"
        />
        <Button
          v-if="!okOnly"
          :label="cancelTitle || t('common.modal.discard')"
          severity="secondary"
          outlined
          data-testid="modal-cancel"
          @click="cancel"
        />
      </slot>
    </template>
  </ModalDocked>
  <Dialog
    v-else
    v-model:visible="visible"
    modal
    :header="title"
    :style="style"
    :dismissable-mask="closeOnBackdrop"
    :closable="closable"
    :close-on-escape="false"
    :draggable="false"
    :pt="{ root: { 'data-kit': 'modal', onKeydownCapture } }"
    @hide="emit('hidden')"
  >
    <slot />
    <template v-if="!hideFooter" #footer>
      <slot name="footer" :ok="ok" :cancel="cancel" :busy="busy">
        <Button
          v-if="!okOnly"
          :label="cancelTitle || t('common.action.cancel')"
          severity="secondary"
          outlined
          data-testid="modal-cancel"
          @click="cancel"
        />
        <Button
          :label="okTitle || t('common.action.confirm')"
          :severity="okVariant === 'danger' ? 'danger' : undefined"
          :disabled="okDisabled"
          :loading="busy"
          data-testid="modal-ok"
          @click="ok"
        />
      </slot>
    </template>
  </Dialog>
</template>
